import React, { useState, useEffect, useCallback, useRef } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const EnhancedImageLightbox = ({ images, labels, currentIndex, onClose, onNavigate }) => {
  const validIndices = images
    .map((img, idx) => (img && img.startsWith("https://") ? idx : -1))
    .filter((idx) => idx !== -1);
  
  const validImages = validIndices.map((idx) => images[idx]);
  const validLabels = validIndices.map((idx) => labels[idx]);

  const mappedIndex = validIndices.indexOf(currentIndex);
  const [activeIndex, setActiveIndex] = useState(mappedIndex >= 0 ? mappedIndex : 0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const thumbnailContainerRef = useRef(null);
  const thumbnailRefs = useRef([]);

  useEffect(() => {
    const newMappedIndex = validIndices.indexOf(currentIndex);
    if (newMappedIndex >= 0) {
      setActiveIndex(newMappedIndex);
    }
  }, [currentIndex, validIndices]);

  useEffect(() => {
    if (thumbnailRefs.current[activeIndex] && thumbnailContainerRef.current) {
      const thumbnail = thumbnailRefs.current[activeIndex];
      const container = thumbnailContainerRef.current;
      
      const thumbnailRect = thumbnail.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      const isInView = 
        thumbnailRect.left >= containerRect.left &&
        thumbnailRect.right <= containerRect.right;
      
      if (!isInView) {
        thumbnail.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [activeIndex]);

  const handlePrevious = useCallback(() => {
    const newIndex = activeIndex === 0 ? validImages.length - 1 : activeIndex - 1;
    setActiveIndex(newIndex);
    if (validIndices[newIndex] !== undefined) {
      onNavigate(validIndices[newIndex]);
    }
  }, [activeIndex, validImages.length, validIndices, onNavigate]);

  const handleNext = useCallback(() => {
    const newIndex = activeIndex === validImages.length - 1 ? 0 : activeIndex + 1;
    setActiveIndex(newIndex);
    if (validIndices[newIndex] !== undefined) {
      onNavigate(validIndices[newIndex]);
    }
  }, [activeIndex, validImages.length, validIndices, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowLeft") {
        handlePrevious();
      } else if (e.key === "ArrowRight") {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleNext, handlePrevious, onClose]);

  const handleThumbnailClick = (index) => {
    setActiveIndex(index);
    if (validIndices[index] !== undefined) {
      onNavigate(validIndices[index]);
    }
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = (e) => {
    if (!touchStart || !touchEnd) return;
    
    // Only handle swipe if it's a single touch (not pinch-to-zoom)
    if (e.changedTouches.length === 1) {
      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > minSwipeDistance;
      const isRightSwipe = distance < -minSwipeDistance;

      if (isLeftSwipe) {
        handleNext();
      } else if (isRightSwipe) {
        handlePrevious();
      }
    }
  };

  if (validImages.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.95)",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        width: "100vw",
                height: "100vh"
      }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(255, 255, 255, 0.1)",
          border: "2px solid rgba(255, 255, 255, 0.3)",
          borderRadius: "50%",
          width: "48px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.3s ease",
          zIndex: 10001,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.5)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
          e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.3)";
        }}
      >
        <CloseIcon style={{ color: "#fff", fontSize: "28px" }} />
      </button>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          maxHeight: "100%",
          padding: window.innerWidth <= 768 ? "70px 0 0 0" : "70px 0 0 0",
          position: "relative",
          marginBottom: window.innerWidth <= 768 ? "250px" : "190px",
          touchAction: "pan-y pinch-zoom",
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            border: "2px solid rgba(255, 255, 255, 0.15)",
            borderRadius: "50%",
            width: window.innerWidth <= 768 ? "44px" : "56px",
            height: window.innerWidth <= 768 ? "44px" : "56px",
            display: window.innerWidth <= 768 ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "absolute",
            left: window.innerWidth <= 768 ? "20px" : "40px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
          }}
        >
          <ChevronLeftIcon style={{ color: "#fff", fontSize: window.innerWidth <= 768 ? "28px" : "32px" }} />
        </button>

        <div
          style={{
            width: "100%",
            maxHeight: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <img
            src={validImages[activeIndex]}
            alt={validLabels[activeIndex]}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: window.innerWidth <= 768 ? "calc(100vh - 150px)" : "calc(100vh - 140px)",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
              touchAction: "auto",
              userSelect: "none",
            }}
          />
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            handleNext();
          }}
          style={{
            background: "rgba(255, 255, 255, 0.05)",
            border: "2px solid rgba(255, 255, 255, 0.15)",
            borderRadius: "50%",
            width: window.innerWidth <= 768 ? "44px" : "56px",
            height: window.innerWidth <= 768 ? "44px" : "56px",
            display: window.innerWidth <= 768 ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.3s ease",
            position: "absolute",
            right: window.innerWidth <= 768 ? "20px" : "40px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.15)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.4)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)";
            e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
          }}
        >
          <ChevronRightIcon style={{ color: "#fff", fontSize: window.innerWidth <= 768 ? "28px" : "32px" }} />
        </button>
      </div>

      <div
        ref={thumbnailContainerRef}
        style={{
          width: "100%",
          padding: window.innerWidth <= 768 ? "24px" : "12px 20px",
          paddingBottom: window.innerWidth <= 768 ? "calc(39px + env(safe-area-inset-bottom))" : "12px",
          backgroundColor: "rgba(0, 0, 0, 0.3)",
          borderTop: "1px solid rgba(255, 255, 255, 0.1)",
          overflowX: "auto",
          overflowY: "hidden",
          position: "fixed",
          bottom: 0,
          left: 0,
          zIndex: 10000,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            justifyContent: "center",
            alignItems: "center",
            minWidth: "min-content",
            padding: "0 20px",
          }}
        >
          {validImages.map((img, index) => {
            const label = validLabels[index];
            const isEntryTimeframe = typeof label === 'object' ? label?.isEntryTimeframe : false;
            const labelText = typeof label === 'object' ? label?.title : label;
            
            return (
              <div
                key={index}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                onClick={() => handleThumbnailClick(index)}
                style={{
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "all 0.3s ease",
                  padding: "8px 16px",
                  borderRadius: "8px",
                  backgroundColor: index === activeIndex ? "rgba(237, 158, 14, 0.2)" : "rgba(255, 255, 255, 0.05)",
                  border: index === activeIndex ? "2px solid #ED9E0E" : "2px solid rgba(255, 255, 255, 0.15)",
                }}
              >
                <p
                  style={{
                    color: isEntryTimeframe ? "#ED9E0E" : (index === activeIndex ? "#ED9E0E" : "#fff"),
                    fontSize: "11px",
                    fontWeight: "600",
                    margin: 0,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  {labelText}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EnhancedImageLightbox;
